'use client';

import { type ChangeEvent } from 'react';

import { Button } from '@/app/components/button';
import { Checkbox } from '@/app/components/forms/checkbox';
import { Icon } from '@/app/components/icons';
import { IconClose } from '@/app/components/icons/ui-actions/icon-close';
import { BaseDealerPanel } from '@/app/components/shared/dealer/components/shared/base-dealer-panel';
import { ADVANCED_SEARCH } from '@/app/components/shared/dealer/utils/dealer-constant';
import { ICON_DEALER_TYPE_MAP } from '@/app/components/shared/dealer/utils/dealer-icons';
import { EVENT_NAME } from '@/app/components/shared/dealer/utils/tracking';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';

const GROUP_NAME =
  'components/shared/dealer/shared/search-panel/advanced-search';

interface SearchPanelAdvancedSearchProps {
  selectedFilterSearch: Set<string>;
  onSelectOptions: (event: ChangeEvent<HTMLInputElement>) => void;
  onClose: () => void;
  onReset: () => void;
  onSearch: () => void;
}

export const SearchPanelAdvancedSearch = ({
  selectedFilterSearch,
  onSelectOptions,
  onClose,
  onReset,
  onSearch,
}: SearchPanelAdvancedSearchProps) => {
  return (
    <BaseDealerPanel
      className="h-screen-header absolute top-0 z-20 xl:relative"
      contentClassName="px-4"
      header={
        <div className="flex shrink-0 items-center justify-between py-1 pr-2 pl-4">
          <p className="text-body-1-fixed font-noto-bold">ตัวกรองการค้นหา</p>
          <Button
            variant="tertiary"
            color="black"
            iconBefore={<IconClose />}
            onClick={onClose}
            aria-label="Close"
            {...createDataAttributes({ test: 'close' })}
          />
        </div>
      }
      footer={
        <>
          <Button
            width="full"
            variant="secondary"
            color="black"
            onClick={onReset}
            {...createDataAttributes({ test: 'reset' })}
            {...createDataGA({
              click: true,
              eventName: EVENT_NAME,
              group: GROUP_NAME,
              text: 'reset',
            })}
            disabled={selectedFilterSearch.size === 0}
          >
            รีเซ็ต{' '}
            {selectedFilterSearch.size > 0
              ? `(${selectedFilterSearch.size})`
              : ''}
          </Button>
          <Button
            width="full"
            onClick={onSearch}
            {...createDataAttributes({ test: 'submit' })}
            {...createDataGA({
              click: true,
              eventName: EVENT_NAME,
              group: GROUP_NAME,
              text: 'submit',
            })}
          >
            ตกลง
          </Button>
        </>
      }
    >
      {ADVANCED_SEARCH.map((item) => {
        const options = item.options || [];

        return (
          <div
            key={item.id}
            className="bg-silver-50 space-y-4 rounded-sm px-4 py-3 last:mb-16 xl:last:mb-0"
            {...createDataAttributes({ test: item.id })}
          >
            {item.description || options.length === 0 ? (
              <Checkbox
                className="w-full flex-row-reverse justify-between"
                key={item.id}
                name={item.id}
                onChangeInput={onSelectOptions}
                options={[
                  {
                    name: item.id,
                    checked: selectedFilterSearch.has(item.id),
                    children: (
                      <div className="inline-flex items-center gap-x-2">
                        {!!item.icon && (
                          <Icon
                            icon={ICON_DEALER_TYPE_MAP[item.icon]}
                            className="size-5"
                          />
                        )}
                        <span className="text-body-1-fixed font-noto-bold">
                          {item.label}
                        </span>
                      </div>
                    ),
                  },
                ]}
              />
            ) : (
              <div className="flex items-center gap-x-2">
                {!!item.icon && (
                  <Icon
                    icon={ICON_DEALER_TYPE_MAP[item.icon]}
                    className="size-5"
                  />
                )}
                <span className="text-body-1-fixed font-noto-bold">
                  {item.label}
                </span>
              </div>
            )}

            {!!item.description && (
              <p className="text-body-2 mb-4 text-gray-600">
                {item.description}
              </p>
            )}

            {options.length > 0 &&
              options.map((option) => {
                return (
                  <Checkbox
                    className="w-full flex-row-reverse justify-between py-0"
                    key={option.name}
                    name={option.name}
                    onChangeInput={onSelectOptions}
                    options={[
                      {
                        checked: selectedFilterSearch.has(option.name),
                        name: option.name,
                        label: option.label,
                      },
                    ]}
                  />
                );
              })}
          </div>
        );
      })}
    </BaseDealerPanel>
  );
};
